// `GET /backoffice/customers/{retailCustomerId}/kyc-sessions` de `retail-wizard-business`: las
// verificaciones de MetaMap vistas desde la venta. Permiso `retail.customer.read`, scope RETAILER.
import { apiFetch } from '../../client';

const BASE_URL = import.meta.env.VITE_API_RETAIL_WIZARD_BASE_URL;

/** Las ocho estaciones de MetaMap, con el nombre con el que el silo las manda. */
export type KycSubCheckName =
    | 'documentReading'
    | 'templateMatching'
    | 'alterationDetection'
    | 'facematch'
    | 'liveness'
    | 'ageCheck'
    | 'watchlists'
    | 'premiumAmlWatchlists';

/**
 * En qué quedó UNA estación. Son cuatro valores, no dos: `NOT_REPORTED` es «no corrió» y `HIT` es
 * «que lo mire cumplimiento». Ninguno de los dos es un rechazo.
 */
export type KycSubCheckOutcome = 'PASSED' | 'FAILED' | 'HIT' | 'NOT_REPORTED';

export interface KycSubCheckResult {
    name: KycSubCheckName;
    outcome: KycSubCheckOutcome;
    /** El estado crudo de MetaMap, o `null` cuando la estación no corrió. */
    status: string | null;
}

/** Desenlace del comprobante de domicilio (`ProofAddressStatusEnum`, doce valores). */
export interface ProofOfAddressResult {
    status: string;
    /** ¿Quedó un domicilio en el expediente del cliente? */
    addressPersisted: boolean;
    verificationId: string;
    /** `false` solo mientras MetaMap lo tiene en revisión manual: el veredicto todavía puede cambiar. */
    terminal: boolean;
}

/** Una sesión de venta vista desde el KYC. Sin identidad: eso es del resumen 360. */
export interface CustomerKycSession {
    sessionId: string;
    storeId: string;
    state: string;
    /** Veredicto global de la identidad. `null` = el KYC de esta sesión todavía no cerró. */
    kycStatus: string | null;
    kycModality: string | null;
    /** SIEMPRE las ocho, en orden fijo: las que no llegaron viajan con `NOT_REPORTED`. */
    subChecks: KycSubCheckResult[];
    proofOfAddress: ProofOfAddressResult | null;
    startedAt: string | null;
    updatedAt: string | null;
}

/** Lista vacía = el cliente no tiene ninguna sesión de venta. No es un error. */
export interface CustomerKycSessionsResult {
    retailCustomerId: string;
    /** De la más reciente a la más vieja. */
    items: CustomerKycSession[];
    count: number;
    /** El cliente tenía más sesiones que el tope y solo llegaron las más recientes. */
    truncated: boolean;
}

/**
 * El alcance de la lectura. El silo se lo IMPONE a un actor de nivel retailer, pero se lo EXIGE por
 * query a uno TENANT, que manda sobre varias cadenas. Vacío cuando no se pasa.
 */
function scopeQuery(retailerId: string | undefined): string {
    return retailerId ? `?${new URLSearchParams({ value: retailerId }).toString()}` : '';
}

export function getCustomerKycSessions(
    retailCustomerId: string,
    retailerId?: string,
): Promise<CustomerKycSessionsResult> {
    return apiFetch<CustomerKycSessionsResult>(
        `/backoffice/customers/${encodeURIComponent(retailCustomerId)}/kyc-sessions${scopeQuery(retailerId)}`,
        { baseUrl: BASE_URL },
    );
}
